import { CommonModule, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  NgZone,
  output,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { HxBaseControl } from '../../base/hx-base-control';
import { ButtonContext, ButtonSize, ButtonVariant } from '../button/button';
import { HxIconButton } from '../icon-button/icon-button';
import { HxIcon } from '../icon/icon';
import { HxPopoverDirective, PopoverPosition } from '../popover/popover';
import { HxTooltipDirective } from '../tooltip/tooltip';
import { ActionGroupConfig, ActionItem, ActionItemClickEvent } from './action-group.types';

const BUTTON_SIZE_MAP: Record<ButtonSize, number> = {
  xs: 24,
  sm: 32,
  md: 40,
  lg: 48,
  xl: 56,
};

/**
 * Harmony Action Group Component (hx-action-group)
 *
 * Dynamically displays action icon buttons within table rows or cards. When container width is constrained,
 * automatically moves overflowing actions into an accessible kebab menu (three-dots-vertical), ensuring actions are
 * always accessible without clipping or manual width adjustments.
 *
 * @example
 *   ```html
 *   <hx-action-group [actions]="actions" [row]="row" />
 *   ```;
 */
@Component({
  selector: 'hx-action-group',
  standalone: true,
  imports: [CommonModule, NgTemplateOutlet, HxIconButton, HxIcon, HxPopoverDirective, HxTooltipDirective],
  templateUrl: './action-group.component.html',
  styleUrls: ['./action-group.component.css'],
  exportAs: 'hxActionGroup',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'hx-action-group',
    '[class.hx-action-group--align-start]': 'align() === "start"',
    '[class.hx-action-group--align-center]': 'align() === "center"',
    '[class.hx-action-group--align-end]': 'align() === "end"',
  },
})
export class HxActionGroupComponent<T = any> extends HxBaseControl implements AfterViewInit {
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);
  private readonly cdr = inject(ChangeDetectorRef);

  // Inputs
  readonly actions = input<ActionItem<T>[]>([]);
  readonly config = input<ActionGroupConfig | undefined>(undefined);
  readonly row = input<T | undefined>(undefined);

  // Outputs
  readonly actionClick = output<ActionItemClickEvent<T>>();
  readonly menuOpened = output<void>();
  readonly menuClosed = output<void>();

  // Internal state
  readonly isMenuOpen = signal<boolean>(false);
  readonly measuredWidth = signal<number>(0);
  private readonly hasMeasuredWidth = signal<boolean>(false);

  // Popover reference
  readonly popoverRef = viewChild(HxPopoverDirective);

  // Config getters
  readonly size = computed<ButtonSize>(() => this.config()?.size || 'sm');
  readonly defaultIconSize = computed<string | undefined>(() => this.config()?.iconSize);
  readonly defaultVariant = computed<ButtonVariant>(() => this.config()?.variant || 'ghost');
  readonly defaultContext = computed<ButtonContext>(() => this.config()?.context || 'neutral');
  readonly gapPx = computed<number>(() => this.config()?.gapPx ?? 4);
  readonly kebabTooltip = computed<string>(() => this.config()?.kebabTooltip || 'More actions');
  readonly kebabAriaLabel = computed<string>(() => this.config()?.kebabAriaLabel || 'More actions');
  readonly kebabPosition = computed<PopoverPosition>(() => this.config()?.kebabPosition || 'bottom-end');
  readonly align = computed<'start' | 'center' | 'end'>(() => this.config()?.align || 'start');

  /** Filtered active actions that are not hidden */
  readonly activeActions = computed<ActionItem<T>[]>(() => {
    const list = this.actions();
    const currentRow = this.row();
    if (!list || list.length === 0) {
      return [];
    }

    return list.filter((item) => {
      if (typeof item.hidden === 'function') {
        return !item.hidden(currentRow);
      }
      return !item.hidden;
    });
  });

  /** Calculated visible direct action buttons */
  readonly visibleActions = computed<ActionItem<T>[]>(() => {
    const active = this.activeActions();
    const count = active.length;
    if (count === 0) {
      return [];
    }

    const maxVisibleConfig = this.config()?.maxVisible;
    if (maxVisibleConfig != null && maxVisibleConfig >= 0) {
      if (maxVisibleConfig >= count) {
        return active;
      }
      return active.slice(0, maxVisibleConfig);
    }

    const availableWidth = this.measuredWidth();
    const buttonWidth = BUTTON_SIZE_MAP[this.size()] || 32;
    const gap = this.gapPx();

    // Keep the initial render stable until the first measurement arrives.
    if (!this.hasMeasuredWidth()) {
      return active;
    }

    // A constrained group must keep room for the kebab instead of allowing direct icons to clip.
    if (availableWidth <= 0 || availableWidth < buttonWidth) {
      return [];
    }

    // Single actions also use the kebab when the column cannot provide a full icon slot.
    if (count <= 1) {
      return active;
    }

    const totalWidthNeeded = count * buttonWidth + (count - 1) * gap;
    if (availableWidth >= totalWidthNeeded) {
      return active;
    }

    // Keep small action groups consistent: do not leave one direct icon when the other action overflows.
    if (count <= 2) {
      return [];
    }

    // When space is constrained, reserve space for the kebab button
    const spaceForIcons = availableWidth - buttonWidth - gap;
    if (spaceForIcons <= 0) {
      // Not enough space for any direct icon + kebab -> move all to kebab
      return [];
    }

    const maxFit = Math.min(count - 1, Math.floor((spaceForIcons + gap) / (buttonWidth + gap)));
    return active.slice(0, Math.max(0, maxFit));
  });

  /** Calculated overflow actions inside kebab dropdown */
  readonly overflowActions = computed<ActionItem<T>[]>(() => {
    const active = this.activeActions();
    const visible = this.visibleActions();
    if (active.length === visible.length) {
      return [];
    }
    return active.slice(visible.length);
  });

  ngAfterViewInit(): void {
    if (typeof window !== 'undefined') {
      const rect = this.elementRef.nativeElement.getBoundingClientRect();
      if (rect) {
        this.measuredWidth.set(rect.width);
        this.hasMeasuredWidth.set(true);
      }

      if (typeof ResizeObserver !== 'undefined') {
        this.ngZone.runOutsideAngular(() => {
          let rafId: number | null = null;
          const observer = new ResizeObserver((entries) => {
            if (!entries.length) return;
            const width = entries[0].contentRect.width;
            if (rafId !== null) {
              cancelAnimationFrame(rafId);
            }
            rafId = requestAnimationFrame(() => {
              if (Math.abs(width - this.measuredWidth()) >= 1) {
                this.ngZone.run(() => {
                  this.measuredWidth.set(width);
                  this.hasMeasuredWidth.set(true);
                  this.cdr.markForCheck();
                });
              }
            });
          });

          observer.observe(this.elementRef.nativeElement);

          this.destroyRef.onDestroy(() => {
            if (rafId !== null) {
              cancelAnimationFrame(rafId);
            }
            observer.disconnect();
          });
        });
      }
    }
  }

  isItemDisabled(item: ActionItem<T>): boolean {
    const currentRow = this.row();
    if (typeof item.disabled === 'function') {
      return item.disabled(currentRow);
    }
    return !!item.disabled;
  }

  onActionItemClick(item: ActionItem<T>, event: MouseEvent | Event): void {
    event.stopPropagation();
    if (this.isItemDisabled(item)) {
      return;
    }

    const currentRow = this.row();
    item.action?.(currentRow, event);
    this.actionClick.emit({
      action: item,
      row: currentRow,
      originalEvent: event,
    });
  }

  onOverflowItemClick(item: ActionItem<T>, event: MouseEvent | Event): void {
    event.stopPropagation();
    if (this.isItemDisabled(item)) {
      return;
    }

    const currentRow = this.row();
    this.popoverRef()?.hide();
    item.action?.(currentRow, event);
    this.actionClick.emit({
      action: item,
      row: currentRow,
      originalEvent: event,
    });
  }

  onPopoverOpened(): void {
    this.isMenuOpen.set(true);
    this.menuOpened.emit();
  }

  onPopoverClosed(): void {
    this.isMenuOpen.set(false);
    this.menuClosed.emit();
  }
}
